import Tooltip from '../../../../../../common/_custom/Tooltip/Tooltip';
import DirectorHoverCard from './DirectorHoverCard';
import DirectorHoverCardEmpty, { DirectorHoverUnmatched } from './DirectorHoverCardEmpty';
import type { DirectorHoverProfile } from './DirectorHoverCard.types';

/**
 * The director hover card's single mounting point.
 *
 * Four surfaces show this card — Related Party, Regulatory Checks, Director Bureau and Management
 * Profile — and the Tooltip needs the same non-obvious wiring on every one of them (portal so it
 * escapes the table's overflow, right-side placement, a hide delay long enough to move the cursor
 * onto the card, no arrow, zero padding so the card owns its own edges). Keeping that in one place
 * is the difference between four surfaces behaving identically and four surfaces drifting apart.
 *
 * With no `profile` it falls back in order: the `unmatched` empty-state card (what the row knows,
 * plus "No data available"), then a plain `fallback` text tooltip, then the children bare — so
 * callers never have to branch on whether the roster join found a match.
 */
function DirectorHoverCardTooltip({
    profile,
    unmatched,
    fallback,
    children,
}: {
    profile?: DirectorHoverProfile;
    /** Row-level facts for a person with no management profile — drives the empty-state card. */
    unmatched?: DirectorHoverUnmatched;
    /** Plain-text tooltip used when there is neither a profile nor `unmatched` (truncated names). */
    fallback?: string;
    children: React.ReactNode;
}) {
    if (!profile && !unmatched) {
        if (!fallback) return <>{children}</>;
        return (
            <Tooltip content={fallback} direction="top" contentCss={{ whiteSpace: 'nowrap' }} fitContent small usePortal>
                {children}
            </Tooltip>
        );
    }

    return (
        <Tooltip
            content={
                profile ? <DirectorHoverCard profile={profile} /> : <DirectorHoverCardEmpty {...(unmatched as DirectorHoverUnmatched)} />
            }
            direction="right"
            contentCss={{ padding: 0 }}
            fitContent
            hideArrow
            hideDelay={150}
            showDelay={1000}
            usePortal
        >
            {children}
        </Tooltip>
    );
}

export default DirectorHoverCardTooltip;
